// 評価タブの他サブ画面（メッセージ/My Card など、detail/styles.module.css を使う
// StudentDetailPanel の兄弟タブ）と見た目のトークンを揃えるため、専用の
// ../styles.module.css ではなく detail 側の共有スタイルを使う。
import Styles from '../detail/styles.module.css';
import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, getFirestore, query, where } from 'firebase/firestore';
import { ApplicantRow, ExamRecord, RecruitSelectionConfig, examinationLabel, overallLabel, scoreLabel } from '../types';

type Props = {
  applicant: ApplicantRow;
  onChanged: (patch: Partial<ApplicantRow>) => void;
  // サロン見学の審査(16.judging)を表示する場合はtrue。選考段階(1〜5次)のような
  // 複数ラウンドはなく、examination=0固定の単一ラウンドとして扱う。
  isVisit?: boolean;
};

// 元: src/onetime/legacy/recruit-evaluation-2025-06/src/desktop/info/manage/index.tsx
// （bar/summary/evaluationの3ファイルを1ファイルに統合したもの）
//
// 以前はここの「選考段階」セレクトが、閲覧する段階の切り替えと同時に
// applicant.examination(実際のステータス)も書き換えていた。パネル上部の
// 「ステータス」セレクト(StudentDetailPanel)と同じ値を別の場所からも変更できて
// しまい、「閲覧」と「ステータス変更」が結びついて分かりにくかったため、
// ここでは実ステータスの変更は行わず、1次選考〜現在の段階までを選んで
// 過去の評価を遡って閲覧できるだけのタブ(stagePills)にしている。
// ステータス自体の変更は引き続きパネル上部からのみ行う。
const EvaluationSummary: React.FC<Props> = ({ applicant, isVisit }) => {

  const [ config, setConfig ] = useState<RecruitSelectionConfig>({ selectionCount: 0, texts: [] });
  const [ examData, setExamData ] = useState<ExamRecord[]>([]);
  // 閲覧中の選考段階(実ステータスとは独立。デフォルトは実ステータスと同じ段階)。
  // 見学は単一ラウンドのため、examination=0固定で段階切り替え自体を行わない。
  const [ viewStage, setViewStage ] = useState<number>(isVisit ? 0 : Math.min(Math.max(applicant.examination, 1), 5));

  useEffect(() => {
    if (isVisit) return;
    // 実ステータスが変わったら、閲覧中の段階もそれに追従させる(採用/不採用の場合は
    // 審査項目が存在する最後の段階=5次選考までしか遡れないため5にクランプする)。
    setViewStage(Math.min(Math.max(applicant.examination, 1), 5));
  }, [applicant.examination, isVisit]);

  useEffect(() => {
    (async () => {
      const db = getFirestore();
      const recruitSnap = await getDoc(doc(db, 'recruits', applicant.recruitId));
      const recruitData = recruitSnap.data();
      const n = viewStage;
      setConfig(isVisit ? {
        selectionCount: recruitData?.visitExamCount ?? 0,
        texts: [1, 2, 3, 4, 5, 6, 7, 8].map((i) => recruitData?.[`visitExamText${i}`] ?? ''),
      } : {
        selectionCount: recruitData?.[`exam_selection${n}Count`] ?? 0,
        texts: [1, 2, 3, 4, 5, 6, 7, 8].map((i) => recruitData?.[`exam_selection${n}Text${i}`] ?? ''),
      });

      await loadExamData(viewStage);
    })();
  }, [applicant.id, applicant.recruitId, viewStage, isVisit]);

  async function loadExamData(evaluation: number) {
    const db = getFirestore();
    // 見学(isVisit)はapplicantsドキュメントが存在しないchat idのため、
    // 16.judging/index.tsxと同じくchats/{id}/visitExaminationを参照する
    // (applicants/{id}/examinationを見るとルールがapplicants doc存在前提のため
    // permission-deniedになる)。
    const examCollectionRef = isVisit
      ? collection(db, 'chats', applicant.id, 'visitExamination')
      : collection(db, 'applicants', applicant.id, 'examination');
    const examQuery = query(
      examCollectionRef,
      where('evaluation', '==', evaluation),
    );
    const snap = await getDocs(examQuery);
    const rows: ExamRecord[] = snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        uid: data.uid ?? '',
        name: data.name ?? '',
        evaluation: data.evaluation ?? 0,
        scores: [1, 2, 3, 4, 5, 6, 7, 8].map((i) => Number(data[`evaluation${i}Score`] ?? 0)),
        comments: [1, 2, 3, 4, 5, 6, 7, 8].map((i) => String(data[`evaluation${i}Comment`] ?? '')),
        // 16.judging/index.tsx の save() が書き込む総合判断フィールド。
        // 旧来の EvaluationForm.tsx 経由の保存にはこれらが無いため、欠損時は
        // overallScore=0・コメント空文字のまま「未入力」表示に倒す。
        overallScore: Number(data.overallScore ?? 0),
        overallComment: String(data.overallComment ?? ''),
        strengthComment: String(data.strengthComment ?? ''),
        concernComment: String(data.concernComment ?? ''),
        draft: !!data.draft,
      };
    });
    setExamData(rows);
  }

  // 審査項目(exam_selectionNCount)が設定され得るのは1〜5次選考のみ。実ステータスが
  // 採用(6)・不採用(7)であっても、そこに至るまでの1〜5次選考の評価は遡って閲覧できる。
  const maxViewableStage = Math.min(Math.max(applicant.examination, 1), 5);
  const viewableStages = [1, 2, 3, 4, 5].filter((s) => s <= maxViewableStage);

  return (
    <div>
      {isVisit ? (
        <div className={Styles.section}>
          <h5 className={Styles.sectionTitle}>サロン見学の審査</h5>
          <p className={Styles.currentStageHint}>選考段階とは別に、サロン見学時点での審査員による評価をまとめて表示します（複数ラウンドはありません）。</p>
        </div>
      ) : (
        <>
          {/* 現在のステータスは詳細上部の進捗ステップで確認・変更できるため、ここには出さない */}
          <div className={Styles.section}>
            <h5 className={Styles.sectionTitle}>選考段階を選んで評価を見る</h5>
            <div className={Styles.stagePills}>
              {viewableStages.map((s) => (
                <button
                  type="button"
                  key={s}
                  className={viewStage === s ? Styles.stagePillActive : Styles.stagePill}
                  onClick={() => setViewStage(s)}
                >
                  {examinationLabel(s)}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <div className={Styles.section}>
        <h5 className={Styles.sectionTitle}>全体評価（{examData.length}名が評価済み）</h5>

        {examData.length === 0 ? (
          <p className={Styles.empty}>この選考段階の評価はまだありません。</p>
        ) : (
          <div className={Styles.judgeList}>
            {examData.map((row) => (
              <div className={Styles.judgeCard} key={row.id}>
                <div className={Styles.judgeCardHeader}>
                  <span className={Styles.judgeCardName}>{row.name || row.uid}</span>
                  {row.draft && <span className={Styles.draftBadge}>下書き</span>}
                  <span className={Styles.overallBadge}>{overallLabel(row.overallScore, isVisit)}</span>
                </div>

                {config.selectionCount > 0 && (
                  <div className={Styles.judgeScoreRow}>
                    {row.scores.slice(0, config.selectionCount).map((s, i) => (
                      <span className={Styles.judgeScoreItem} key={i}>
                        <b>{scoreLabel(s)}</b>{config.texts[i] || `審査${i + 1}`}
                      </span>
                    ))}
                  </div>
                )}

                {row.comments.slice(0, config.selectionCount).some((c) => c.trim() !== '') && (
                  <div>
                    {row.comments.slice(0, config.selectionCount).map((c, i) => c.trim() !== '' && (
                      <p className={Styles.judgeComment} key={i}>
                        {config.texts[i] || `審査${i + 1}`}：{c}
                      </p>
                    ))}
                  </div>
                )}

                {row.overallComment.trim() !== '' && (
                  <p className={Styles.judgeOverallComment}>{row.overallComment}</p>
                )}

                {row.strengthComment.trim() !== '' && (
                  <div className={Styles.fieldRow}>
                    <span className={Styles.fieldLabel}>強み</span>
                    <span className={Styles.fieldValue}>{row.strengthComment}</span>
                  </div>
                )}

                {row.concernComment.trim() !== '' && (
                  <div className={Styles.fieldRow}>
                    <span className={Styles.fieldLabel}>懸念点</span>
                    <span className={Styles.fieldValue}>{row.concernComment}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EvaluationSummary;
